(() => {
  'use strict';
  if (customElements.get('miniami-inline-description')) return;
  // Normalize only rendered BR/marker paragraphs; merchant descriptionHtml stays untouched.
  const formatDescription = rte => {
    // Readiness is diagnostic, never a guard: editors can replace the same HTML.
    if (!rte) return;
    rte.querySelectorAll('p,div').forEach(paragraph => {
      // Keep merchant containers and already-semantic lists intact; format leaf runs only.
      if (paragraph.closest('ul,ol,li') || paragraph.querySelector('p,div,ul,ol,li,img,video,audio,iframe,input,svg')) return;
      const lines = [document.createDocumentFragment()];
      const visit = (node, ancestors = []) => {
        if (node.nodeType === Node.TEXT_NODE) {
          let copy = node.cloneNode();
          ancestors.slice().reverse().forEach(parent => { const wrapper = parent.cloneNode(); wrapper.append(copy); copy = wrapper; });
          lines[lines.length - 1].append(copy);
        } else if (node.nodeName === 'BR') lines.push(document.createDocumentFragment());
        else if (node.nodeType === Node.ELEMENT_NODE) node.childNodes.forEach(child => visit(child, [...ancestors, node]));
      };
      paragraph.childNodes.forEach(node => visit(node));
      if (!lines.some(line => /^\s*[*•-]\s+/.test(line.textContent))) return;
      const output = document.createDocumentFragment();
      let list = null, marker = '', parentItem = null, nested = null;
      lines.forEach(line => {
        const match = line.textContent.match(/^\s*([*•-])\s+/);
        if (!match) {
          list = null; nested = null; parentItem = null;
          if (!line.textContent.trim()) return;
          const p = paragraph.cloneNode(); p.append(line); output.append(p); return;
        }
        // Remove marker syntax only, including markers split across inline spans.
        let remaining = match[0].length;
        const walker = document.createTreeWalker(line, NodeFilter.SHOW_TEXT);
        while (remaining) { const text = walker.nextNode(); if (!text) break; const take = Math.min(remaining, text.length); text.deleteData(0, take); remaining -= take; }
        const item = document.createElement('li'); item.append(line);
        if (match[1] === '-' && marker === '*' && parentItem) {
          if (!nested) { nested = document.createElement('ul'); parentItem.append(nested); }
          nested.append(item);
        } else {
          if (!list || marker !== match[1]) { list = document.createElement('ul'); output.append(list); }
          list.append(item); marker = match[1]; parentItem = item; nested = null;
        }
      });
      paragraph.replaceWith(output);
    });
  };
  customElements.define('miniami-inline-description', class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      const text = this.querySelector('.miniami-inline-description__text');
      const viewport = this.querySelector('.miniami-inline-description__viewport');
      const button = this.querySelector('.miniami-inline-description__toggle');
      if (!text || !viewport || !button) return;
      // The native markup is the reversible source. There is only one live text
      // copy: collapsed prefix + hidden suffix, or complete semantic author HTML.
      const nativeMarkup = viewport.innerHTML;
      const disclosure = viewport.querySelector('details');
      const initiallyExpanded = !!disclosure?.open;
      // Replace native disclosure synchronously; no intermediate full frame paints.
      if (disclosure) { viewport.prepend(text); disclosure.remove(); }
      const nativeHTML = text.innerHTML;
      const source = document.createElement('div');
      source.innerHTML = nativeHTML;
      formatDescription(source);
      const fullHTML = source.innerHTML;
      // Block boundaries become spaces in the compact excerpt, not new copy.
      source.querySelectorAll('p,div,h1,h2,h3,h4,h5,h6,li,ul,ol,blockquote,br').forEach(node => {
        node.before(document.createTextNode(' ')); node.after(document.createTextNode(' '));
      });
      const positions = [];
      const walker = document.createTreeWalker(source, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        const words = node.textContent.matchAll(/\S+\s*/gu);
        for (const word of words) positions.push({ node, offset:word.index + word[0].length });
      }
      const reduce = matchMedia('(prefers-reduced-motion: reduce)');
      const label = button.querySelector('[data-description-label]');
      let expanded = initiallyExpanded, animation = null, frame = 0, width = 0, truncated = false;
      const stop = () => { animation?.cancel(); animation = null; viewport.style.overflow = ''; };
      const moveFocus = wasFocused => { if (wasFocused && button.isConnected) button.focus({ preventScroll:true }); };
      const full = () => {
        const wasFocused = document.activeElement === button;
        button.remove(); text.innerHTML = fullHTML; viewport.append(button);
        this.dataset.state = 'expanded'; button.setAttribute('aria-expanded','true');
        if (label) label.textContent = 'See less'; button.hidden = !truncated;
        moveFocus(wasFocused);
      };
      const excerpt = count => {
        const end = positions[count - 1];
        const prefix = document.createRange(); prefix.selectNodeContents(source);
        prefix.setEnd(end.node,end.offset);
        const suffix = document.createRange(); suffix.selectNodeContents(source);
        suffix.setStart(end.node,end.offset);
        const tail = document.createElement('span');
        tail.className = 'miniami-inline-description__tail'; tail.hidden = true; tail.inert = true;
        tail.append(suffix.cloneContents());
        const head = prefix.cloneContents();
        // Range may clone a boundary ancestor. Native IDs belong to the full
        // description only; do not create duplicate fragment IDs while collapsed.
        head.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
        tail.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
        text.replaceChildren(head,button,tail);
      };
      const sameLine = () => {
        const texts = document.createTreeWalker(text,NodeFilter.SHOW_TEXT,{
          acceptNode:n => n.parentElement.closest('button,[hidden]') || !n.textContent.trim() ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
        });
        let last = null, next;
        while ((next = texts.nextNode())) last = next;
        if (!last) return true;
        const end = last.textContent.trimEnd().length;
        const range = document.createRange(); range.setStart(last,Math.max(0,end - 1)); range.setEnd(last,end);
        const rect = range.getBoundingClientRect(), control = button.getBoundingClientRect();
        return Math.abs(rect.top - control.top) < 5;
      };
      const collapse = () => {
        const wasFocused = document.activeElement === button;
        stop(); full(); button.hidden = true;
        const line = parseFloat(getComputedStyle(text).lineHeight);
        const lines = Number(this.dataset.lines) || 3;
        if (!positions.length || text.getBoundingClientRect().height <= line * lines + 1) {
          truncated = false; this.dataset.state = 'short'; button.hidden = true;
          this.dataset.layoutWidth = String(text.getBoundingClientRect().width);
          this.dataset.ready = 'true'; return;
        }
        truncated = true; this.dataset.state = 'collapsed';
        button.hidden = false; button.setAttribute('aria-expanded','false');
        if (label) label.textContent = 'Read more';
        let low = 1, high = positions.length, best = 1;
        while (low <= high) {
          const middle = Math.floor((low + high) / 2); excerpt(middle);
          if (text.getBoundingClientRect().height <= line * lines + 1) { best = middle; low = middle + 1; }
          else high = middle - 1;
        }
        excerpt(best);
        // Prefer the last native word and Read more on the same third line,
        // rather than a lonely control on a new line.
        while (best > 1 && !sameLine()) excerpt(--best);
        this.dataset.excerptWords = String(best);
        this.dataset.layoutWidth = String(text.getBoundingClientRect().width);
        this.dataset.ready = 'true';
        moveFocus(wasFocused);
      };
      const settle = () => { stop(); if (expanded) full(); else collapse(); };
      const toggle = () => {
        // Safari need not focus a native button on pointer activation. Move
        // focus only for this real user action, never at startup or from tests.
        button.focus({ preventScroll:true });
        const from = viewport.getBoundingClientRect().height;
        stop(); expanded = !expanded;
        if (expanded) full(); else collapse();
        const to = viewport.getBoundingClientRect().height;
        if (reduce.matches || !viewport.animate || Math.abs(to - from) < 1) return;
        viewport.style.overflow = 'hidden';
        const active = viewport.animate([{ height:`${from}px` },{ height:`${to}px` }],{duration:260,easing:'cubic-bezier(.2,.7,.2,1)'});
        animation = active;
        active.finished.then(() => { if (animation === active) stop(); },() => {});
      };
      const reflow = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          if (!this.isConnected || !text.getBoundingClientRect().width) return;
          if (expanded) { stop(); full(); } else collapse();
        });
      };
      const observer = new ResizeObserver(entries => {
        const nextWidth = entries[0].contentRect.width;
        if (Math.abs(nextWidth - width) > .5) {
          width = nextWidth; cancelAnimationFrame(frame);
          // Refit before the resized frame paints. A delayed extra rAF can
          // briefly expose four/five lines on a desktop-to-mobile reversal.
          if (nextWidth && this.isConnected) { if (expanded) { stop(); full(); } else collapse(); }
        }
      });
      observer.observe(text); button.addEventListener('click',toggle); reduce.addEventListener('change',settle);
      if (text.getBoundingClientRect().width) { collapse(); if (expanded) full(); }
      document.fonts?.ready.then(() => { if (this.isConnected && this.cleanup) reflow(); });
      this.cleanup = () => {
        observer.disconnect(); cancelAnimationFrame(frame); stop();
        button.removeEventListener('click',toggle); reduce.removeEventListener('change',settle);
        // Return genuine, complete no-enhancement markup on editor removal.
        viewport.innerHTML = nativeMarkup;
        const restored = viewport.querySelector('details');
        if (restored) restored.open = expanded;
        delete this.dataset.ready; delete this.dataset.state;
        this.cleanup = null;
      };
    }
    disconnectedCallback() { this.cleanup?.(); }
  });
})();
